import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@saleshandy/design-system';
import { ArrowRight, Cross, Help } from '@saleshandy/icons';
import Modal from '../../../../../../shared/design-system/components/atoms/modal';
import { SHLogoIcon } from '../../../../../../shared/svg';
import { supportUrls } from '../../../../../../shared/utils/urls';
import { AnalyticsEvents } from '../../../../../../shared/enums/analytics';
import { capturePosthogEvent } from '../../../../../../shared/utils/initialize-third-party-integrations';
import {
  getUserShAccountId,
  getUserLeadFinderPlanCode,
  getUserPlanCode,
} from '../../../../../../shared/utils/user-details';
import { RootState } from '../../../../../../store/root-reducer';
import StepIndicator from './step-indicator';
import OverviewStep from './overview-step';
import InstallStep from './install-step';
import FinishStep from './finish-step';
import {
  LEAD_FINDER_EXTENSION_ZIP_NAME,
  LEAD_FINDER_EXTENSION_ZIP_URL,
} from './constants';
import { dismissLeadFinderExtensionBanner } from '../../../../../v2-lead/components/lead-finder-extension-banner/banner-dismissal';

// Where the wizard was launched from — sent to PostHog so we can see which
// entry point drives installs.
export type LeadFinderExtensionModalSource =
  | 'banner'
  | 'empty_screen'
  | 'download_button';

type IProps = {
  show: boolean;
  onClose: () => void;
  source: LeadFinderExtensionModalSource;
};

const FIRST_STEP = 1;
const INSTALL_STEP = 2;
const FINISH_STEP = 3;

// Kick off the extension ZIP download programmatically (used to auto-start the
// download as soon as the wizard opens, so the file is ready by the time the
// user reaches the install steps).
const triggerExtensionDownload = () => {
  const link = document.createElement('a');
  link.href = LEAD_FINDER_EXTENSION_ZIP_URL;
  link.download = LEAD_FINDER_EXTENSION_ZIP_NAME;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const LeadFinderExtensionModal: React.FC<IProps> = ({
  show,
  onClose,
  source,
}) => {
  const creditGiven = useSelector(
    (state: RootState) => state.home.creditGiven,
  );
  const [step, setStep] = useState(FIRST_STEP);

  // Reset to the first step and auto-start the ZIP download whenever the modal
  // is opened. Opening the wizard from any entry point also suppresses the
  // install banner for the current login (same as the banner's "Install now")
  // and reports the launch (with its source) to PostHog.
  useEffect(() => {
    if (show) {
      setStep(FIRST_STEP);
      triggerExtensionDownload();
      dismissLeadFinderExtensionBanner();
      capturePosthogEvent(AnalyticsEvents.LeadFinderExtensionWizardOpened, {
        source,
        shAccountId: getUserShAccountId(),
        leadFinderPlan: getUserLeadFinderPlanCode(),
        outreachPlan: getUserPlanCode(),
        current_tab_url: document.location.href,
      });
    }
  }, [show, source]);

  const handleClose = () => {
    setStep(FIRST_STEP);
    onClose();
  };

  const renderBody = () => {
    if (step === INSTALL_STEP) {
      return <InstallStep />;
    }
    if (step === FINISH_STEP) {
      return <FinishStep creditGiven={creditGiven} />;
    }
    return <OverviewStep creditGiven={creditGiven} />;
  };

  const renderFooter = () => {
    if (step === FINISH_STEP) {
      return (
        <>
          <a
            className="bs-lf-ext__guide-link regular-2 gray-txt-11"
            href={supportUrls.leadFinderExtensionGuide}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Help width={16} height={16} />
            Need help? Read the guide
          </a>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </>
      );
    }

    return (
      <>
        <button
          type="button"
          className="bs-lf-ext__text-btn"
          onClick={() => (step === INSTALL_STEP ? setStep(FIRST_STEP) : handleClose())}
        >
          {step === INSTALL_STEP ? '← Back' : 'Cancel'}
        </button>
        <Button
          onClick={() =>
            setStep(step === INSTALL_STEP ? FINISH_STEP : INSTALL_STEP)
          }
        >
          {step === INSTALL_STEP ? "I've installed it" : 'Continue'}
          <ArrowRight width={16} height={16} className="ml-1" />
        </Button>
      </>
    );
  };

  return (
    <Modal
      show={show}
      onClose={handleClose}
      onHide={handleClose}
      hideHeader
      hideFooter
      className="lead-finder-extension-modal"
      showCloseIcon={false}
      extraModalProps={{
        'aria-labelledby': 'lead-finder-extension-title',
        centered: true,
        backdrop: 'static',
      }}
    >
      <div className="bs-lf-ext">
        <div className="bs-lf-ext__header">
          <span className="bs-lf-ext__logo" aria-hidden>
            <SHLogoIcon />
          </span>
          <div className="bs-lf-ext__titles">
            <h2
              id="lead-finder-extension-title"
              className="bs-lf-ext__title semibold-4 gray-txt-9"
            >
              The new Lead Finder Chrome extension is here.
            </h2>
            <p className="bs-lf-ext__subtitle regular-2 gray-txt-11">
              Install takes three minutes. You only do this once.
            </p>
          </div>
          <button
            type="button"
            className="bs-lf-ext__close"
            aria-label="Close"
            onClick={handleClose}
          >
            <Cross width={20} height={20} className="gray-txt-9" />
          </button>
        </div>

        <StepIndicator currentStep={step} onStepClick={setStep} />

        <div className="bs-lf-ext__divider" />

        <div className="bs-lf-ext__body">{renderBody()}</div>

        <div className="bs-lf-ext__footer">{renderFooter()}</div>
      </div>
    </Modal>
  );
};

export default LeadFinderExtensionModal;
